import { AnyFieldApi, AnyFormApi } from '@tanstack/react-form-nextjs'
import { AxiosError } from 'axios'
import { toast } from 'sonner'

import { scrollToFirstInvalidField } from '@/components/form/form-utils'

/**
 * Checks if a field is invalid
 * @param field - The field instance
 * @returns True if the field is invalid, false otherwise
 */
export const isFieldInvalid = (field: AnyFieldApi) => {
  return (
    field.state.meta.isTouched &&
    !field.state.meta.isValid &&
    !!field.state.meta.errors.length
  )
}

export const parseDateForInput = (dateString: null | string): null | string => {
  if (!dateString) return null
  try {
    // If it's already in YYYY-MM-DD format, return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString
    }
    // Otherwise, parse and format
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return null
    return date.toISOString().split('T')[0]
  } catch {
    return null
  }
}

export const apiFormValidation = async (
  mutationFn: () => Promise<unknown>,
  formApi: AnyFormApi
): Promise<boolean> => {
  // Clear any server errors carried over from a previous failed attempt so
  // re-submitting after a fix isn't blocked by stale onSubmit errors.
  formApi.setErrorMap({ onSubmit: undefined })

  try {
    await mutationFn()
    return true
  } catch (error) {
    const errors = (error as AxiosError<API>)?.response?.data.errors

    if (errors) {
      const errorMap: Record<string, string> = {}

      // Laravel-style array errors are keyed like "passengers.1.user_id";
      // tanstack-form addresses array fields as "passengers[1].user_id".
      const toFieldPath = (key: string) => key.replace(/\.(\d+)(?=\.|$)/g, '[$1]')

      Object.entries(errors).forEach(([fieldName, errorMessages]) => {
        if (Array.isArray(errorMessages)) {
          const errorMessage = errorMessages.join(', ')
          errorMap[toFieldPath(fieldName)] = errorMessage
        }
      })

      // Set error map first
      formApi.setErrorMap({ onSubmit: { fields: errorMap } })

      // Touch all fields with errors so they display
      Object.keys(errorMap).forEach((fieldName) => {
        try {
          formApi.setFieldMeta(fieldName, (prev) => ({
            ...prev,
            isTouched: true
          }))
        } catch {
          // Field might not exist yet or path might be invalid, that's okay
        }
      })

      // Show toast with validation errors
      const errorMessages = Object.values(errorMap)
      if (errorMessages.length > 0) {
        if (errorMessages.length === 1) {
          toast.error(errorMessages[0])
        } else {
          const errorSummary = errorMessages.slice(0, 3).join(', ')
          const remainingCount = errorMessages.length - 3
          const message =
            remainingCount > 0
              ? `${errorSummary}${remainingCount > 0 ? ` and ${remainingCount} more error${remainingCount > 1 ? 's' : ''}` : ''}`
              : errorSummary
          toast.error(message)
        }
      }

      // Scroll to first invalid field after React re-renders
      scrollToFirstInvalidField(100)

      return false
    }

    // Non-validation error (network, 5xx, etc.) — surface a generic toast so
    // the user knows something happened, but don't rethrow. Rethrowing leaves
    // tanstack-form in a "rejected submission" state that blocks subsequent
    // submits until the form is remounted.
    const fallbackMessage =
      (error as AxiosError<API>)?.response?.data?.message ||
      (error as Error)?.message
    if (fallbackMessage) {
      toast.error(fallbackMessage)
    }
    return false
  }
}
